// Membership helpers — mirror of the backend's User.has_active_access logic.
export const TRIAL_DAYS = 14

/**
 * Check if user has any active access (membership or unexpired trial)
 * @param {Object} user - User with trial, membership, membershipUntil fields
 * @returns {boolean}
 */
export const hasActiveAccess = (user) => {
  if (!user) return false

  if (user.membership) return true

  // Trial or cancelled-membership grace period still running
  if (user.membershipUntil) {
    return new Date(user.membershipUntil) > new Date()
  }

  return false
}

/**
 * Check if user is currently in trial period
 */
export const isInTrial = (user) => {
  if (!user || !user.trial || user.membership) return false

  if (user.membershipUntil) {
    return new Date(user.membershipUntil) > new Date()
  }

  return false
}

/**
 * Days remaining for trial or membership access (0 if none)
 */
export const getDaysRemaining = (user) => {
  if (!user || !user.membershipUntil) return 0

  const now = new Date()
  const expiryDate = new Date(user.membershipUntil)

  if (expiryDate <= now) return 0

  return Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
}

/**
 * Membership status as a string: 'member' | 'trial' | 'expired' | 'none'
 */
export const getMembershipStatus = (user) => {
  if (!user) return 'none'

  if (user.membership) return 'member'

  if (isInTrial(user)) return 'trial'

  if (user.trial || (user.membershipUntil && new Date(user.membershipUntil) <= new Date())) {
    return 'expired'
  }

  return 'none'
}

/**
 * Trial progress as a percentage (0-100)
 */
export const getTrialProgress = (user) => {
  if (!isInTrial(user)) return 0

  const remainingDays = getDaysRemaining(user)
  const elapsedDays = TRIAL_DAYS - remainingDays

  return Math.max(0, Math.min(100, (elapsedDays / TRIAL_DAYS) * 100))
}

export default {
  TRIAL_DAYS,
  hasActiveAccess,
  isInTrial,
  getDaysRemaining,
  getMembershipStatus,
  getTrialProgress,
}
